<template>
  <div class="OutOfService" data-testid="out-of-service-root">
    <img class="OutOfService__logo" :src="LOGO">

    <h1 class="OutOfService__title kiosk-pre-line">{{ outOfServiceMessage }}</h1>
  </div>
</template>


<script>
import { useUiStore } from '@/app/stores/ui';
import LOGO from '@/assets/images/theartisan-logo.svg';
import { idleWatcher } from '@/services/IdleWatcher';
import { session } from '@/services/Session';
import { sentryService } from '@/services/telemetry/sentry';
import { warnMessageOf } from '@/shared/utils/track-telemetry';

export default {
  setup() {
    return { ui: useUiStore() };
  },
  data() {
    return {
      LOGO,
    };
  },
  created() {
    // Non-reactive properties: pause handles and promise references must not
    // be wrapped in Vue's reactivity proxy.
    this._idleWatcherPause = null;
    // Track the in-flight session.end() promise so beforeUnmount can await it
    // when the kiosk recovers quickly and navigates away from OOS.
    this._endSessionPromise = null;
  },
  computed: {
    /** Render the OOS message without v-html (XSS-safe). Uses CSS white-space
     *  via the existing kiosk-pre-line class to preserve line breaks. */
    outOfServiceMessage() {
      return this.$te('OutOfService.message')
        ? this.$t('OutOfService.message')
        : 'Out of Service';
    },
  },
  mounted() {
    // Clear any lingering UI overlays (error popups, loading indicators) from
    // the session that was active when the kiosk went out of service. Without
    // this, an error popup from the dying session stays visible on top of OOS.
    this.ui.overlayQueue = [];

    // If an active user session exists when the kiosk transitions to OOS
    // (e.g., navigated here by external kiosk management software via Vue
    // router, or by a printer-jam detection), end it cleanly to prevent
    // orphaned Sentry sessions, dangling API state, and stale store data.
    //
    // The promise is stored so beforeUnmount can await it if the kiosk
    // recovers before end() finishes (prevents create() racing with a
    // still-in-flight end() on the Session singleton).
    if (session.id) {
      sentryService.addBreadcrumb('oos', 'Out-of-service with active session', {
        // Full session ID is intentional Sentry metadata for DB joins.
        session_id: session.id,
      });

      this._endSessionPromise = session
        .end('error')
        .catch((e) => warnMessageOf('[OutOfService] session.end() failed:', e));
    }

    // Pause idle watcher -- there is no user session on the OOS screen,
    // so idle reset would be meaningless and could cause navigation loops.
    this._idleWatcherPause = idleWatcher.pause();

    sentryService.addBreadcrumb('oos', 'Out-of-service screen displayed');

    // RECOVERY NOTE: This component has no self-recovery mechanism.
    // Navigation away from /402 is driven externally by the kiosk management
    // software (e.g., Electron shell, remote management API) which pushes a
    // new URL when the hardware issue is resolved. This is by design -- the
    // frontend should not second-guess the management layer about whether
    // the kiosk is operational.
  },
  async beforeUnmount() {
    // If session.end() is still in flight (kiosk recovered quickly after
    // going OOS), await it so the Session singleton's _isEnding mutex is
    // fully released before the next component mounts and calls create().
    if (this._endSessionPromise) {
      try {
        await this._endSessionPromise;
      } catch (_e) {
        // Already logged in the .catch() handler above
      }
      this._endSessionPromise = null;
    }

    // Resume idle watcher when leaving OOS (kiosk recovered)
    this._idleWatcherPause?.continue();
    this._idleWatcherPause = null;
  },
};
</script>


<style scoped lang="scss">
  .OutOfService {
    background: var(--Global--tint-color);
    color: var(--Global--tint-inverse-color);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 4rem;
    min-height: 100vh;
    // Prevent interaction with any elements that might bleed through
    // from the previous screen (developer panel button, etc.)
    position: relative;
    z-index: 1;
  }

  .OutOfService__logo {
    width: 15rem;
  }

  .OutOfService__title {
    max-width: 70%;
    font-size: 2rem;
    text-align: center;
    font-weight: 300;
  }
</style>
